import {
  faCheckCircle,
  faExclamationTriangle,
  faHourglass,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { SpinnerIcon } from '~/components/common/SpinnerIcon';
import { Button } from '~/components/ui/button';
import { TextAreaInput } from '~/components/ui/forms/text-area-input';
import { TextInput } from '~/components/ui/forms/text-input';
import type { PictureUploadQueueFormValues } from '~/components/upload/file/picture-upload-v4';
import { PictureTypeSelect } from '~/components/upload/supporting-object/picture-type-select-v4';
import { assertExhaustive, cn } from '~/utils/common';

export function PictureUploadQueueItem({
  index,
  onRemove,
}: {
  index: number;
  onRemove: () => unknown;
}) {
  const form = useFormContext<PictureUploadQueueFormValues>();
  const { setValue } = form;

  const defaultAuthor = form.watch('defaultValues.author');
  const item = form.watch(`items.${index}`);
  const isEditable = item.status === 'pending' || item.status === 'failed';

  // Memoize the URL so the pictures don't flicker when state updated
  const pictureUrl = useMemo(() => {
    return URL.createObjectURL(item.file);
  }, [item.file]);

  useEffect(() => {
    if (isEditable) {
      setValue(`items.${index}.values.author`, defaultAuthor);
    }
  }, [defaultAuthor, isEditable, index, setValue]);

  const autofillValues = form.watch('autofillValues');
  const shouldAutofill = form.watch('defaultValues.autofill');

  useEffect(() => {
    if (shouldAutofill && autofillValues && isEditable) {
      const { name, description, author } = autofillValues;
      if (name?.length) {
        setValue(`items.${index}.values.name`, name);
      }
      if (description?.length) {
        setValue(`items.${index}.values.description`, description);
      }
      if (author?.length) {
        setValue(`items.${index}.values.author`, author);
      }
    }
  }, [shouldAutofill, autofillValues, isEditable, index, setValue]);

  return (
    <FormProvider {...form}>
      <div
        className={cn('border p-4', {
          'border-slate-200 bg-transparent': item.status === 'pending',
          'border-success bg-emerald-50': item.status === 'success',
          'border-error bg-rose-50': item.status === 'failed',
        })}
      >
        <div className="flex gap-4 items-center">
          <div className="space-y-1">
            <div
              className="w-24 h-24 bg-cover bg-center"
              style={{
                backgroundImage: `url("${pictureUrl}")`,
              }}
            />
            {isEditable && (
              <div className="text-center">
                <Button
                  type="button"
                  onClick={() => onRemove()}
                  color="ghost"
                  size="xs"
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          <div className="grow space-y-2">
            <TextInput
              {...form.register(`items.${index}.values.name`)}
              label="Name"
              placeholder={item.file.name}
              required
              className="input-sm"
            />
            <TextAreaInput
              {...form.register(`items.${index}.values.description`)}
              label="Description"
              required
              className="input-sm"
            />

            <div className="grid grid-cols-2 gap-4">
              <TextInput
                {...form.register(`items.${index}.values.author`)}
                label="Author"
                className="input-sm"
              />
              <PictureTypeSelect
                {...form.register(`items.${index}.values.type`)}
                label="Type"
                className="select select-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-center shrink">
            <ItemStatusIcon status={item.status} />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

function ItemStatusIcon({
  status,
}: {
  status: PictureUploadQueueFormValues['items'][number]['status'];
}) {
  switch (status) {
    case 'pending':
      return (
        <FontAwesomeIcon
          icon={faHourglass}
          className="text-3xl text-slate-300"
        />
      );
    case 'success':
      return (
        <FontAwesomeIcon
          icon={faCheckCircle}
          className="text-3xl text-success"
        />
      );
    case 'failed':
      return (
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-3xl text-error"
        />
      );
    case 'uploading':
      return <SpinnerIcon className="text-3xl text-slate-800" />;
    default:
      assertExhaustive(status, `Item status ${status} not handled`);
      return null;
  }
}
